import { cn } from '../../../lib/cn';
import { signedPoints } from '../../../lib/format';
import { shortRider, type DuelVM } from '../../../lib/cyclingRound';
import { Check, ChevronRight, Clock, X } from '../../../components/ui/icons';

function rowTone(vm: DuelVM, stageLive: boolean): string {
  if (vm.status === 'void') return 'bg-[#F8F8FA]';
  if (vm.status === 'settled' && vm.correct) return 'bg-accent-wash/40';
  if (vm.status === 'settled' && vm.correct === false) return 'bg-[rgba(210,84,79,0.06)]';
  if (stageLive && vm.status === 'upcoming') return 'bg-[rgba(227,168,43,0.06)]';
  return '';
}

/** Compact duel row inside the grouped list — stake once on the left, result on the right. */
export function StageDuelRow({
  vm,
  stageLive,
  onOpen,
  last = false
}: {
  vm: DuelVM;
  stageLive: boolean;
  onOpen: () => void;
  last?: boolean;
}) {
  const won = vm.status === 'settled' && !!vm.correct;
  const lost = vm.status === 'settled' && vm.correct === false;
  const decided = vm.status !== 'upcoming';
  const aBacked = vm.pick === 'A';

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'tap flex w-full items-center gap-2.5 px-3 py-[11px] text-left active:bg-black/[0.03]',
        rowTone(vm, stageLive),
        !last && 'border-b border-black/[0.06]'
      )}
    >
      <span
        className={cn(
          'num grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[8px] text-[11px] font-extrabold',
          won ? 'bg-night text-white' : 'bg-[#EFEFF3] text-ink2'
        )}
      >
        <span className="num text-[10px] font-extrabold leading-none text-ink2">×{vm.confidence}</span>
      </span>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-extrabold tracking-[-0.01em] text-ink">
          <span className={cn(aBacked ? 'text-accent-ink' : 'text-ink3')}>{shortRider(vm.riderAName)}</span>
          <span className="px-1 font-semibold text-ink4">v</span>
          <span className={cn(!aBacked ? 'text-accent-ink' : 'text-ink3')}>{shortRider(vm.riderBName)}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1 truncate text-[10.5px] font-semibold text-ink3">
          {vm.status === 'void' ? (
            'Void'
          ) : won ? (
            <span className="inline-flex items-center gap-0.5 text-accent-ink">
              <Check width={10} height={10} strokeWidth={3.2} /> Won
            </span>
          ) : lost ? (
            <span className="inline-flex items-center gap-0.5 text-miss">
              <X width={10} height={10} strokeWidth={3.2} /> Lost
            </span>
          ) : stageLive ? (
            <span className="inline-flex items-center gap-0.5 text-[#B5740A]">
              <Clock width={10} height={10} strokeWidth={2.2} /> Live
            </span>
          ) : (
            <span className="text-ink2">×{vm.confidence} stake</span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {decided && vm.points != null ? (
          <span className={cn('num text-[15px] font-extrabold leading-none', won ? 'text-accent-ink' : lost ? 'text-miss' : 'text-ink')}>
            {signedPoints(vm.points)}
          </span>
        ) : null}
        <ChevronRight width={14} height={14} strokeWidth={2.4} className="text-ink4" />
      </div>
    </button>
  );
}
